import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, Clock, Copy, Lock, Pencil, Sparkles, Users, Wand2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { RequestAffiliateModal } from '@/components/account/RequestAffiliateModal';
import { accountsService } from '@/services/accounts.service';
import { env } from '@/lib/env';
import { useAccountStore } from '@/store/account.store';
import type { AffiliateSummary, AffiliateMonth } from '@/types/api';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * AffiliateSection — Account-page card for the referral program.
 *
 * Gating flows top-down (open to any account — no paid-plan gate):
 *
 *   1. Approved affiliate (or admin): the full `AffiliateDashboard` —
 *      share link, headline count, historical picker. Admins always
 *      pass through so they can test the surface, mirroring the
 *      server-side `AffiliateApprovedGuard` (approved OR admin).
 *
 *   2. Not yet approved (free or paid): a locked card with a state-specific
 *      control — `none` opens the request modal, `pending` shows
 *      "under review", `rejected` shows a terminal "not approved".
 *      Eligibility is decided by manual admin review of the application.
 *
 * The dashboard summary is fetched on mount inside `AffiliateDashboard`.
 */
export function AffiliateSection() {
  const affiliateStatus = useAccountStore((s) => s.account?.affiliateStatus ?? 'none');
  const isAdmin = useAccountStore((s) => s.isAdmin());

  if (isAdmin || affiliateStatus === 'approved') {
    return <AffiliateDashboard />;
  }

  return <AffiliateLockedCard status={affiliateStatus} />;
}

/**
 * Locked affiliate card for accounts (free or paid) that have not been approved.
 * The control under the blurb depends on the application state:
 *   - `none`     → "Request to Become an Affiliate" → opens the modal.
 *   - `pending`  → disabled "Request received — under review".
 *   - `rejected` → disabled "Request was not approved" (terminal; only an
 *                  admin can reset it, so there is no re-apply path here).
 */
function AffiliateLockedCard({ status }: { status: 'none' | 'pending' | 'rejected' }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-or-amber-500" aria-hidden />
            <div className="font-display text-[16px] font-bold text-or-slate-900">
              Affiliate program
            </div>
          </div>
          <p className="mt-1 text-[13px] text-or-slate-600">
            Share OllieReads with other families and earn 50% commission when they start a paid
            subscription. Request access to get your link and set up payouts.
          </p>
        </div>
      </div>

      <div className="mt-5">
        {status === 'pending' ? (
          <div className="inline-flex items-center gap-2 rounded-xl border border-or-orange-200 bg-or-orange-50 px-4 py-3 text-[13px] font-medium text-or-orange-700">
            <Clock className="h-4 w-4" aria-hidden />
            Request received — under review
          </div>
        ) : status === 'rejected' ? (
          <div className="inline-flex items-center gap-2 rounded-xl border border-or-slate-200 bg-or-slate-50 px-4 py-3 text-[13px] font-medium text-or-slate-600">
            <XCircle className="h-4 w-4" aria-hidden />
            Request was not approved
          </div>
        ) : (
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            <Lock className="h-4 w-4" aria-hidden />
            Request to Become an Affiliate
          </Button>
        )}
      </div>

      <RequestAffiliateModal open={modalOpen} onOpenChange={setModalOpen} />
    </section>
  );
}

function AffiliateDashboard() {
  const [summary, setSummary] = useState<AffiliateSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    accountsService
      .getAffiliate()
      .then((data) => {
        if (!alive) return;
        setSummary(data);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const message = err instanceof Error ? err.message : 'Failed to load affiliate data.';
        setLoadError(message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Prefer the vanity slug when one is claimed; the permanent hex code is the
  // fallback (and both resolve to the same account, so either link works).
  const link = summary ? buildAffiliateLink(summary.slug ?? summary.code) : '';

  // Years to show in the dropdown: every year that has a signup OR a click
  // record, plus the current year, sorted newest first. Always includes the
  // current year so the user sees a non-empty picker even before any
  // attribution lands.
  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear]);
    summary?.monthlySignups.forEach((m) => years.add(m.year));
    summary?.monthlyClicks.forEach((m) => years.add(m.year));
    return [...years].sort((a, b) => b - a);
  }, [summary, currentYear]);

  const lookupCount = (year: number, month: number): number => {
    if (!summary) return 0;
    return countFor(summary.monthlySignups, year, month);
  };

  const lookupClicks = (year: number, month: number): number => {
    if (!summary) return 0;
    return countFor(summary.monthlyClicks, year, month);
  };

  const thisMonthCount = lookupCount(currentYear, currentMonth);
  const thisMonthClicks = lookupClicks(currentYear, currentMonth);
  const selectedCount = lookupCount(selectedYear, selectedMonth);
  const selectedClicks = lookupClicks(selectedYear, selectedMonth);
  const isSelectedFuture =
    selectedYear > currentYear ||
    (selectedYear === currentYear && selectedMonth > currentMonth);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error("Couldn't copy — copy the link manually.");
    }
  };

  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-or-teal-600" aria-hidden />
            <div className="font-display text-[16px] font-bold text-or-slate-900">
              Affiliate program
            </div>
          </div>
          <p className="mt-1 text-[13px] text-or-slate-600">
            Share your link with friends and family. Earn 50% commission for every referred
            account that starts a paid OllieReads subscription.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6">
          <InlineLoader label="Loading affiliate stats…" />
        </div>
      ) : loadError ? (
        <div className="mt-6 rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-3 text-[13px] text-or-rose-700">
          {loadError}
        </div>
      ) : summary ? (
        <>
          {/* Share link + copy ------------------------------------------ */}
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code
              className="flex-1 truncate rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-3 font-mono text-[13px] text-or-slate-700"
              aria-label="Your affiliate link"
            >
              {link}
            </code>
            <Button type="button" variant="secondary" size="md" onClick={handleCopy}>
              <Copy className="h-4 w-4" aria-hidden />
              Copy link
            </Button>
          </div>

          {/* Vanity slug editor ---------------------------------------- */}
          <SlugEditor summary={summary} onChange={setSummary} />

          {/* Current-month headline: clicks → paid → conversion --------- */}
          <div className="mt-6 text-[12px] font-semibold uppercase tracking-wide text-or-slate-500">
            {MONTH_NAMES[currentMonth - 1]} {currentYear}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile
              label="Clicks"
              value={thisMonthClicks}
              caption={`unique ${thisMonthClicks === 1 ? 'visitor' : 'visitors'}`}
            />
            <StatTile
              label="Paid signups"
              value={thisMonthCount}
              caption={thisMonthCount === 1 ? 'paid signup' : 'paid signups'}
            />
            <StatTile
              label="Conversion rate"
              value={formatConversion(thisMonthCount, thisMonthClicks)}
              caption="paid ÷ clicks"
              accent
            />
          </div>

          {/* Historical picker ----------------------------------------- */}
          <div className="mt-4 rounded-xl border border-or-slate-100 bg-white px-4 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-or-slate-500">
                Past months
              </div>
              <div className="flex gap-2">
                <select
                  aria-label="Year"
                  className="rounded-lg border border-or-slate-200 bg-white px-2 py-1.5 text-[13px] text-or-slate-800"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Month"
                  className="rounded-lg border border-or-slate-200 bg-white px-2 py-1.5 text-[13px] text-or-slate-800"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((label, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {isSelectedFuture ? (
              <div className="mt-3 text-[13px] text-or-slate-500">No data yet for this month.</div>
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <StatTile label="Clicks" value={selectedClicks} caption="unique visitors" compact />
                <StatTile
                  label="Paid signups"
                  value={selectedCount}
                  caption={selectedCount === 1 ? 'paid signup' : 'paid signups'}
                  compact
                />
                <StatTile
                  label="Conversion"
                  value={formatConversion(selectedCount, selectedClicks)}
                  caption="paid ÷ clicks"
                  compact
                />
              </div>
            )}
          </div>

          <p className="mt-4 text-[12px] text-or-slate-500">
            Clicks are unique monthly visitors to your link (deduped by device); conversion is that
            month's paid signups divided by its clicks, so treat it as an estimate. Only referred
            accounts on an active paid subscription count as signups — a referral that later cancels
            drops off. Commissions are reviewed and paid out manually; reach out to support with any
            questions.
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Client-side mirror of the server's `isValidAffiliateSlug` — gives instant
 * feedback before the request. The server re-validates and owns the final
 * word (including the uniqueness check), so this only needs to catch the
 * obvious cases and return a friendly message for each.
 */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function slugValidationError(value: string): string | null {
  if (value.length < 3) return 'Use at least 3 characters.';
  if (value.length > 32) return 'Keep it to 32 characters or fewer.';
  if (!SLUG_PATTERN.test(value)) {
    return 'Use lowercase letters, numbers, and single hyphens (no spaces or symbols).';
  }
  if (/^[0-9a-f]{10}$/.test(value)) {
    return 'That looks like a code — pick something more memorable.';
  }
  return null;
}

/**
 * Vanity-slug editor for the affiliate dashboard. Lets an approved affiliate
 * claim a memorable link name (e.g. `readwithme`) or revert to the default
 * hex code. Optimistically nothing — it waits for the server's refreshed
 * `AffiliateSummary` (with the new `slug`) and lifts it up via `onChange` so
 * the share link + copy button re-render. Old hex links always keep working,
 * which the helper copy calls out.
 */
function SlugEditor({
  summary,
  onChange,
}: {
  summary: AffiliateSummary;
  onChange: (next: AffiliateSummary) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openEditor = () => {
    setValue(summary.slug ?? '');
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    const next = value.trim();
    const localError = slugValidationError(next);
    if (localError) {
      setError(localError);
      return;
    }
    // No-op save (unchanged slug) — just close.
    if (next === summary.slug) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await accountsService.setAffiliateSlug(next);
      onChange(updated);
      setEditing(false);
      toast.success('Custom link saved');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save your custom link.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await accountsService.clearAffiliateSlug();
      onChange(updated);
      setValue('');
      setEditing(false);
      toast.success('Reverted to your default link');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not remove your custom link.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mt-3 flex flex-col gap-2 rounded-xl border border-or-slate-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-[13px] text-or-slate-600">
          <Wand2 className="h-4 w-4 shrink-0 text-or-teal-600" aria-hidden />
          {summary.slug ? (
            <span>
              Custom link:{' '}
              <span className="font-semibold text-or-slate-800">{summary.slug}</span>
            </span>
          ) : (
            <span>Personalize your link with a custom name that’s easier to share.</span>
          )}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={openEditor}>
          <Pencil className="h-4 w-4" aria-hidden />
          {summary.slug ? 'Edit' : 'Customize'}
        </Button>
      </div>
    );
  }

  const previewLink = buildAffiliateLink(value.trim() || summary.code);

  return (
    <div className="mt-3 rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-4">
      <label
        htmlFor="affiliate-slug"
        className="text-[11px] font-semibold uppercase tracking-wide text-or-slate-500"
      >
        Custom link name
      </label>
      <input
        id="affiliate-slug"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(e) => {
          // Strip anything that can't appear in a slug as they type; the
          // remaining shape checks (length, edge hyphens) run on save.
          setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
          if (error) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
          }
        }}
        placeholder="readwithme"
        maxLength={32}
        disabled={saving}
        className="mt-2 w-full rounded-lg border border-or-slate-200 bg-white px-3 py-2 font-mono text-[13px] text-or-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-teal-500/40"
      />

      <p className="mt-2 break-all text-[12px] text-or-slate-500">
        Preview: <span className="font-mono text-or-slate-600">{previewLink}</span>
      </p>

      {error ? (
        <p className="mt-2 text-[12px] font-medium text-or-rose-700">{error}</p>
      ) : (
        <p className="mt-2 text-[12px] text-or-slate-500">
          3–32 characters: lowercase letters, numbers, and hyphens. Changing your custom name
          retires the old one, but your original link always keeps working.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          <Check className="h-4 w-4" aria-hidden />
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        {summary.slug ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={handleRemove}
            disabled={saving}
            className="ml-auto"
          >
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Build the shareable link. Prefers `VITE_AFFILIATE_BASE_URL` (set to
 * `https://app.olliereads.org` in production) so the link the user
 * copies points at the live app regardless of which environment they
 * happen to be viewing the dashboard from. Falls back to the current
 * origin for local dev.
 *
 * Targets the `/invite` referral landing page (see `pages/Invite.tsx`).
 * The `?ref=<code>` capture is path-agnostic — `captureReferralFromUrl()`
 * runs at boot on any route — so links shared before this change (which
 * pointed at `/?ref=`) still attribute correctly.
 */
export function buildAffiliateLink(code: string): string {
  const base =
    env.affiliateBaseUrl ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  // Strip any trailing slash to keep the produced URL tidy.
  const normalizedBase = base.replace(/\/+$/, '');
  return `${normalizedBase}/invite?ref=${code}`;
}

function countFor(months: AffiliateMonth[], year: number, month: number): number {
  const hit = months.find((m) => m.year === year && m.month === month);
  return hit?.count ?? 0;
}

/**
 * A single metric tile (Clicks / Paid signups / Conversion). `accent` tints it
 * for the headline conversion stat; `compact` shrinks the value for the dense
 * three-up row inside the historical picker.
 */
function StatTile({
  label,
  value,
  caption,
  accent = false,
  compact = false,
}: {
  label: string;
  value: ReactNode;
  caption: string;
  accent?: boolean;
  compact?: boolean;
}) {
  const container = accent
    ? 'rounded-xl border border-or-green-100 bg-or-green-100 px-4 py-4'
    : 'rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-4';
  const valueSize = compact ? 'text-[22px]' : 'text-[28px]';
  return (
    <div className={container}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-or-slate-500">
        {label}
      </div>
      <div className={`mt-1 font-display ${valueSize} font-bold text-or-slate-900`}>{value}</div>
      <div className="text-[12px] text-or-slate-600">{caption}</div>
    </div>
  );
}

/**
 * Click → paid conversion for a month: paid signups ÷ unique clicks, rounded
 * to a whole percent. Returns an em dash when there were no clicks (avoids a
 * divide-by-zero and a meaningless 0%). Can read above 100% when a paid signup
 * clicked in an earlier month than they converted — that's expected given the
 * two metrics bucket by different events, and the UI copy calls it an estimate.
 */
function formatConversion(paid: number, clicks: number): string {
  if (clicks <= 0) return '—';
  return `${Math.round((paid / clicks) * 100)}%`;
}
